import {
  AssetType,
  FireTVCampaignAutomationClient,
  PresignedUrlAction,
  TransformationTemplate,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown, KatFileUpload } from '@amzn/katal-react';
import mime from 'mime-types';
import { ValidationProps } from 'src/components/pages/campaignAutomation/input/assets';
import { ASSET_CONFIG } from 'src/configs/assetConfig';
import { AssetConstants, ErrorConstants, KatalErrorConstants } from 'src/constants';
import { AssetUploadError, ValidationError } from 'src/errors/CustomErrors';
import { fetchUrlMetadata } from 'src/helpers';
import { logger } from 'src/logger';
import { AssetState, AssetStatus, IAssetInputHandler, AssetDetails } from 'src/types';
import { getPresignedUrls } from 'src/utils/clients/controlPlane/apiMethods';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { formatFileSize } from 'src/utils/formatFileSize';
import { AssetMetadataSpecs, DimensionSpecs } from 'src/utils/Types';
import { validateAssetMetadata } from 'src/utils/ValidationUtils';
import { v4 as uuid } from 'uuid';

export interface AssetInputHandlerProps {
  allowedContentTypes: string[];
  assetKeyPrefix: string;
  assetType: AssetType;
  controlPlaneClient: FireTVCampaignAutomationClient;
  setState: (assetState: Partial<AssetState>) => void;
  setAssetDetails: (assetDetails: Partial<AssetDetails>) => void;
  getTransformationTemplate: () => TransformationTemplate;
  validationProps?: Partial<Record<TransformationTemplate, ValidationProps>>;
}

const TIMEOUT_IN_MS = 5000;
const FALLBACK_FILE_NAME = 'unnamed';

/**
 * Abstract base class for handling asset input operations with validation and upload capabilities.
 * Implements the IAssetInputHandler interface to provide consistent asset handling behavior.
 */
export class AssetInputHandler implements IAssetInputHandler {
  protected readonly controlPlaneClient: FireTVCampaignAutomationClient;
  private readonly assetKeyPrefix: string;
  private readonly allowedContentTypes: string[];
  private readonly setState: (assetState: Partial<AssetState>) => void;
  private readonly setAssetDetails: (assetDetails: Partial<AssetDetails>) => void;
  private readonly getTransformationTemplate: () => TransformationTemplate;
  protected readonly assetType: AssetType;

  constructor({
    assetKeyPrefix,
    assetType,
    setState,
    setAssetDetails,
    getTransformationTemplate,
    controlPlaneClient,
    allowedContentTypes,
  }: AssetInputHandlerProps) {
    this.assetKeyPrefix = assetKeyPrefix;
    this.assetType = assetType;
    this.setState = setState;
    this.setAssetDetails = setAssetDetails;
    this.getTransformationTemplate = getTransformationTemplate;
    this.allowedContentTypes = allowedContentTypes;
    this.controlPlaneClient = controlPlaneClient;

    this.handleUrl = this.handleUrl.bind(this);
    this.handleFileAttached = this.handleFileAttached.bind(this);
    this.resetAssetState = this.resetAssetState.bind(this);
    this.handleFileReplaced = this.handleFileReplaced.bind(this);
    this.handleFileAttachedError = this.handleFileAttachedError.bind(this);
    this.setInProgress = this.setInProgress.bind(this);
    this.clearError = this.clearError.bind(this);
    this.onTransformationTemplateChange = this.onTransformationTemplateChange.bind(this);
  }

  /**
   * Clears any error state in the asset handler
   */
  clearError(): void {
    logger.debug('Clearing error');
    this.setState({ error: undefined });
  }

  /**
   * Handles file attachment events from KatFileUpload component
   * @param {KatFileUpload.FilesAttachedEvent} fileAttachedEvent - Event containing attached file information
   * @returns {Promise<void>}
   */
  async handleFileAttached(fileAttachedEvent: KatFileUpload.FilesAttachedEvent): Promise<void> {
    logger.debug('File attached', { fileAttachedEvent });
    this.setInProgress();
    try {
      const file = fileAttachedEvent.detail.files[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileAttachedEvent.detail.files[0]?.name;
      const errorMsg = this.fetchErrorMessage(error, fileName);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
        key: undefined,
      });
    }
  }

  onTransformationTemplateChange(
    e: KatDropdown.ChangeEvent,
    assetDetails: AssetDetails,
    status: AssetStatus,
  ): void {
    const newValue = e.detail.value as TransformationTemplate;
    this.setState({
      transformationTemplate: newValue,
      key: undefined,
      error: undefined,
      status: assetDetails.key ? AssetStatus.PENDING_VALIDATION : AssetStatus.PENDING_INPUT,
    });
  }

  /**
   * Handles file attachment error events
   * @param {KatFileUpload.FilesAttachedErrorEvent} fileAttachedErrorEvent - Error event from file attachment
   */
  handleFileAttachedError(fileAttachedErrorEvent: KatFileUpload.FilesAttachedErrorEvent): void {
    logger.error('File attached error', { fileAttachedErrorEvent });
    const errorDetail = fileAttachedErrorEvent.detail;
    const fileInfo = errorDetail.files?.[0]?.file;

    switch (errorDetail.error) {
      case KatalErrorConstants.KATAL_MAX_SIZE_ERROR:
        const MAX_FILE_SIZE_IN_BYTES =
          ASSET_CONFIG[this.assetType].validationProps?.[this.getTransformationTemplate()]
            ?.maxFileSizeInBytes;
        if (!MAX_FILE_SIZE_IN_BYTES) {
          throw new ValidationError(
            `Missing max file size prop for asset type ${this.assetType} with template ${this.getTransformationTemplate()}`,
          );
        }
        const maxSizeErrorMessage = `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : Size ${fileInfo?.size ? formatFileSize(fileInfo.size) : 'unknown'} exceeds the maximum file size limit ${formatFileSize(MAX_FILE_SIZE_IN_BYTES)}`;
        logger.error('File size exceeds maximum limit', {
          fileInfo,
          maxSize: MAX_FILE_SIZE_IN_BYTES / 1024,
        });
        // Set the state with a specific error message for file size limit exceeded
        this.setState({
          status: AssetStatus.BASIC_VALIDATION_FAILED,
          error: maxSizeErrorMessage,
          key: undefined,
        });
        break;

      case KatalErrorConstants.KATAL_UNSUPPORTED_FILE_ERROR:
        const unsupportedTypeErrorMessage = `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Allowed types: ${this.allowedContentTypes.join(', ')}`;
        logger.error('Unsupported file type', {
          fileInfo,
          allowedTypes: this.allowedContentTypes,
        });
        // Set the state with a specific error message for unsupported file types
        this.setState({
          status: AssetStatus.BASIC_VALIDATION_FAILED,
          error: unsupportedTypeErrorMessage,
          key: undefined,
        });
        break;

      default:
        // For any other error Katal will show the error message in the UI
        // Log the error for debugging purposes
        logger.error(ErrorConstants.MESSAGES.FILE_ATTACHMENT_ERROR, { errorDetail });
        this.setState({
          status: AssetStatus.FAILED,
          error: errorDetail.error,
          key: undefined,
        });
        break;
    }
  }

  /**
   * Resets the asset state to its initial values
   */
  resetAssetState(): void {
    logger.debug('Resetting asset state');
    this.setState({
      status: AssetStatus.PENDING_INPUT,
      error: undefined,
      key: undefined,
      logoTitle: undefined,
      logoDescription: undefined,
      cta: undefined,
      providerLogo: undefined,
      startTime: undefined,
      endTime: undefined,
      videoDuration: undefined,
    });
    this.setAssetDetails({ key: undefined, file: undefined });
  }

  /**
   * Handles file replacement events
   * @param {KatFileUpload.FilesReplacedEvent} fileReplacedEvent - Event containing new file information
   * @returns {Promise<void>}
   */
  async handleFileReplaced(fileReplacedEvent: KatFileUpload.FilesReplacedEvent): Promise<void> {
    logger.debug('File replaced', { fileReplacedEvent });
    this.setInProgress();
    try {
      const file = fileReplacedEvent.detail.newFiles[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileReplacedEvent.detail.newFiles[0]?.name;
      const errorMsg = this.fetchErrorMessage(error, fileName);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
        key: undefined,
      });
    }
  }

  /**
   * Processes a URL input for asset handling
   * @param {string} urlInput - URL to process
   * @returns {Promise<void>}
   */
  async handleUrl(urlInput: string): Promise<void> {
    logger.debug('Handling URL', { urlInput });
    this.setInProgress();
    try {
      const maxFileSizeInBytes = ASSET_CONFIG[this.assetType].globalMaxFileSizeInBytes;
      const urlMetadata = await fetchUrlMetadata(urlInput, TIMEOUT_IN_MS);
      const specs: AssetMetadataSpecs = {
        maxSize: maxFileSizeInBytes,
        allowedTypes: this.allowedContentTypes,
      };
      validateAssetMetadata(urlMetadata, specs);
      const fileObject = await this.fetchUrlFileObject(urlInput);
      await this.handleFileObject(fileObject);
    } catch (error: unknown) {
      this.setState({
        status: AssetStatus.BASIC_VALIDATION_FAILED,
        error: getErrorMessage(error),
        key: undefined,
      });
    }
  }

  /**
   * Fetches a presigned URL for file upload
   * @param {File} fileObject - File to get presigned URL for
   * @returns {Promise<{key: string, presignedUrl: string}>}
   * @throws {Error} When control plane client is not provided or presigned URL fetch fails
   * @private
   */
  private async fetchPresignedUrl(
    fileObject: File,
  ): Promise<{ key: string; presignedUrl: string }> {
    logger.debug('Fetching presigned URL from control plane', {
      fileObject,
      assetKeyPrefix: this.assetKeyPrefix,
    });

    const key = this.generateS3Key(this.assetKeyPrefix, fileObject);
    try {
      const [presignedUrlResult] = await getPresignedUrls(
        this.controlPlaneClient,
        PresignedUrlAction.PUT,
        [key],
      );

      return presignedUrlResult;
    } catch (error) {
      logger.error('Error occurred while fetching presigned URL', { error });
      throw error;
    }
  }

  /**
   * Fetches a file from a URL and converts it to a File object
   * @param {string} url - URL to fetch file from
   * @returns {Promise<File>}
   * @throws {Error} When fetch fails or content type is missing
   * @private
   */
  private async fetchUrlFileObject(url: string): Promise<File> {
    try {
      const urlObject = new URL(url);
      const response = await fetch(urlObject);
      if (!response.ok) {
        throw new Error(
          `${ErrorConstants.MESSAGES.HTTP_ERROR}: { status: ${response.status} - ${response.statusText} }`,
        );
      }
      const contentType = response.headers.get('content-type');
      if (!contentType) {
        throw new Error('Content type not provided by server');
      }
      const blob = await response.blob();
      const filenameWithExtension = this.getFileNameWithExtension(urlObject.pathname, contentType);
      return new File([blob], filenameWithExtension, {
        type: contentType,
        lastModified: Date.now(),
      });
    } catch (error) {
      const err = new Error(`Failed to fetch file from ${url}: ${getErrorMessage(error)}`);
      err.cause = error;
      throw err;
    }
  }

  /**
   * Generates a unique S3 key for file storage
   * @param {string} keyPrefix - Prefix for the S3 key
   * @param {File} fileObject - File object to generate key for
   * @returns {string} Generated S3 key
   * @private
   */
  private generateS3Key(keyPrefix: string, fileObject: File): string {
    const uniqueId = uuid();
    const originalFilename = fileObject.name;
    let sanitizedName = this.sanitizeFilename(originalFilename);
    const extension = originalFilename.split('.').pop() || '';
    const timestamp = Date.now();
    sanitizedName = sanitizedName.substring(0, AssetConstants.MAX_FILENAME_LENGTH);
    return `${keyPrefix}/${timestamp}_${uniqueId}_${sanitizedName}${extension ? '.' + extension : ''}`;
  }

  /**
   * Generates a filename with appropriate extension based on content type
   * @param {string} pathname - Original pathname
   * @param {string} contentType - MIME type of the content
   * @returns {string} Generated filename with extension
   * @private
   */
  private getFileNameWithExtension(pathname: string, contentType: string): string {
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1).split('.')[0];
    const extension = mime.extension(contentType) || 'bin';
    return `${fileName}.${extension}`;
  }

  /**
   * Processes a file object through validation and upload
   * @param {File} fileObject - File to process
   * @returns {Promise<void>}
   * @private
   */
  private async handleFileObject(fileObject: File): Promise<void> {
    const { presignedUrl, key } = await this.fetchPresignedUrl(fileObject);
    await this.uploadUsingPresignedUrl(presignedUrl, fileObject);
    this.setAssetDetails({
      key,
      file: fileObject,
    });
    this.setState({
      status: AssetStatus.PENDING_VALIDATION,
      key: undefined,
      error: undefined,
    });
  }

  /**
   * Sanitizes a filename for safe storage
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   * @private
   */
  private sanitizeFilename(filename: string): string {
    try {
      // Handle filenames without extensions properly
      const lastDotIndex = filename.lastIndexOf('.');
      const nameWithoutExtension =
        lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;

      const encoded = encodeURIComponent(nameWithoutExtension);
      const sanitized = encoded
        .replace(/%20/g, '_')
        .replace(/%[0-9A-Fa-f]{2}/g, '_')
        .replace(/[^a-zA-Z0-9_.-]/g, '_');

      // Ensure non-empty result
      return sanitized || FALLBACK_FILE_NAME;
    } catch (error) {
      logger.error('Error sanitizing filename', { error });
      return FALLBACK_FILE_NAME;
    }
  }

  /**
   * Sets the asset state to in-progress
   * @private
   */
  private setInProgress(): void {
    this.setState({ status: AssetStatus.PENDING_UPLOAD, error: undefined, key: undefined });
  }

  /**
   * Uploads a file using a presigned URL
   * @param {string} presignedUrl - Presigned URL for upload
   * @param {File} fileObject - File to upload
   * @returns {Promise<void>}
   * @throws {Error} When upload fails
   * @private
   */
  private async uploadUsingPresignedUrl(presignedUrl: string, fileObject: File): Promise<void> {
    logger.debug('Uploading file using presigned URL', { presignedUrl });
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileObject,
        headers: {
          'Content-Type': fileObject.type,
        },
      });

      if (!response.ok) {
        throw new Error(`${ErrorConstants.MESSAGES.UPLOAD_FAILED_WITH_STATUS}: ${response.status}`);
      }
      logger.debug('File uploaded successfully', { response });
    } catch (error) {
      logger.error('Error uploading file', { error: getErrorMessage(error) });
      throw new AssetUploadError(
        `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : ${getErrorMessage(error)}`,
      );
    }
  }

  private fetchErrorMessage(error: unknown, fileName: string | undefined): string {
    if (error instanceof AssetUploadError) return getErrorMessage(error);
    return `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : ${getErrorMessage(error)}`;
  }
}
